
import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Users } from 'lucide-react';
import { useUnifiedChat } from '@/hooks/useUnifiedChat';
import { useChatGroupSelection } from '@/hooks/useChatGroupSelection';
import MobileChatGroupsView from './MobileChatGroupsView';
import MobileChatView from './MobileChatView';
import CreateChatModal from './CreateChatModal';

interface MobileChatInterfaceProps {
  initialGroupId?: string;
  highlightMessageId?: string;
}

const MobileChatInterface = ({ initialGroupId, highlightMessageId }: MobileChatInterfaceProps) => {
  const [viewMode, setViewMode] = useState<'groups' | 'chat'>('groups');
  const [showCreateModal, setShowCreateModal] = useState(false);
  
  const { 
    chatGroups, 
    selectedGroup, 
    messages, 
    sendMessage, 
    selectGroup, 
    canCreateGroup,
    user,
    loading
  } = useUnifiedChat();

  // Auto-select group from URL parameter
  React.useEffect(() => {
    if (initialGroupId && chatGroups.length > 0 && !selectedGroup) {
      console.log('🎯 Auto-selecting group from URL:', initialGroupId);
      const targetGroup = chatGroups.find(group => group.chat_id === initialGroupId);
      if (targetGroup) {
        selectGroup(targetGroup);
        setViewMode('chat');
        console.log('✅ Auto-selected group:', targetGroup.chat_name);
      } else {
        console.warn('⚠️ Group not found:', initialGroupId);
      }
    }
  }, [initialGroupId, chatGroups, selectedGroup, selectGroup]);

  // Handle group selection from notifications
  useChatGroupSelection({
    onSelectGroup: (group) => {
      console.log('=== MOBILE: SELECTING GROUP FROM NOTIFICATION ===');
      console.log('Group to select:', group);
      // Add missing properties to match ChatGroup type
      const fullGroup = {
        ...group,
        updated_at: group.created_at,
        is_active: true
      };
      selectGroup(fullGroup);
      setViewMode('chat'); // Switch to chat view when group is selected from notification
    }
  });

  // For testing purposes, let's make canCreateGroup more permissive
  const canCreate = user && (canCreateGroup || (user as any).role === 'coach' || (user as any).role === 'team-owner' || (user as any).role === 'player');

  const handleSelectGroup = async (group: any) => {
    await selectGroup(group);
    setViewMode('chat');
  };

  const handleBackToGroups = () => {
    setViewMode('groups');
  };

  const handleCreateGroup = () => {
    setShowCreateModal(true);
  };

  const handleCloseModal = () => {
    setShowCreateModal(false);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full bg-black">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-sports-green mx-auto mb-4"></div>
          <p className="text-gray-300">Loading chat...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="flex items-center justify-center h-full bg-black">
        <div className="text-center">
          <Users className="h-12 w-12 mx-auto text-gray-400 mb-4" />
          <p className="text-gray-300 mb-4">Please log in to access chat</p>
          <Button asChild className="bg-sports-green hover:bg-sports-green/90 text-black">
            <a href="/login">Sign In</a>
          </Button>
        </div>
      </div>
    );
  }

  if (viewMode === 'chat') {
    return (
      <>
        <MobileChatView
          selectedGroup={selectedGroup}
          messages={messages}
          user={user}
          sendMessage={sendMessage}
          canCreateGroup={canCreate}
          onBackToGroups={handleBackToGroups}
          onCreateGroup={handleCreateGroup}
          highlightMessageId={highlightMessageId}
        />
        <CreateChatModal
          isOpen={showCreateModal}
          onClose={handleCloseModal}
        />
      </>
    );
  }

  return (
    <>
      <MobileChatGroupsView
        chatGroups={chatGroups}
        selectedGroup={selectedGroup}
        onSelectGroup={handleSelectGroup}
        canCreateGroup={canCreate}
      />
      <CreateChatModal
        isOpen={showCreateModal}
        onClose={handleCloseModal}
      />
    </>
  );
};

export default MobileChatInterface;
